import React, { useEffect, useState } from 'react';
import { TextField, Grid, Button, Box, Paper } from '@mui/material';
import PropTypes from 'prop-types';
import {
  BtnBold,
  BtnBulletList,
  BtnClearFormatting,
  BtnItalic,
  BtnLink,
  BtnNumberedList,
  BtnStrikeThrough,
  BtnUnderline,
  Editor,
  EditorProvider,
  HtmlButton,
  Toolbar,
} from 'react-simple-wysiwyg';
import { useForm, Controller } from 'react-hook-form';

import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  getPageContentByType,
  updatePageContentByType,
} from '../../services/content.service';
import { WEB_PAGE_TYPE } from '../../config/const.config';

const ManagePageContent = ({ pageType }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      title: '',
      content: '',
    },
  });

  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const getPageType = () => {
    if (pageType === WEB_PAGE_TYPE.PRIVACY_POLICY) {
      return 'Privacy policy details updated successfully.';
    }
    if (pageType === WEB_PAGE_TYPE.TERMS_CONDITIONS) {
      return 'Terms and conditions details updated successfully.';
    }
    if (pageType === WEB_PAGE_TYPE.ABOUT_US) {
      return 'About us details updated successfully.';
    }
    return '';
  };

  const updateExistingData = (payload) => {
    updatePageContentByType(payload)
      .then((res) => {
        if (res?.data?.success) {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: getPageType(),
          });
          setFormSubmitted(false);
        }
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      title: data.title.trim(),
      content: data.content,
      pageType,
    };
    updateExistingData(payload);
  };

  useEffect(() => {
    getPageContentByType(pageType)
      .then((res) => {
        // Fill form values
        setValue('title', res.data?.title || '');
        setValue('content', res.data?.content || '');
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, []);

  return (
    <div style={{ marginTop: '30px', marginBottom: '30px' }}>
      <Paper elevation={3} sx={{ padding: 4 }}>
        {loading && <Loading />}

        {!loading && (
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={2} direction="column">
              <Grid
                item
                size={{
                  xs: 12,
                  sm: 12,
                  md: 12,
                }}
              >
                <Controller
                  name="title"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...{ ...maxLengthValidation(255) },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Title"
                      type="text"
                      error={!!errors.title}
                      helperText={errors?.title?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                size={{
                  xs: 12,
                  sm: 12,
                  md: 12,
                }}
              >
                <Controller
                  name="content"
                  control={control}
                  rules={{
                    required: 'This field is required.',
                  }}
                  render={({ field: { value, onChange } }) => (
                    <EditorProvider>
                      <Editor
                        value={value}
                        onChange={onChange}
                        style={{
                          minHeight: '400px',
                          maxHeight: '550px',
                          overflow: 'auto',
                        }}
                      >
                        <Toolbar style={{ backgroundColor: '#ffffff' }}>
                          <Box
                            padding="0px 8px"
                            color="#637381"
                            borderRight="1px solid #637381"
                            fontSize="15px"
                          >
                            Content
                          </Box>
                          <button
                            type="button"
                            onClick={() => document.execCommand('undo')}
                            style={{ marginLeft: '10px' }}
                          >
                            Undo
                          </button>

                          <button
                            type="button"
                            onClick={() => document.execCommand('redo')}
                            style={{ marginLeft: '10px', marginRight: '10px' }}
                          >
                            Redo
                          </button>
                          <BtnBold />
                          <BtnItalic />
                          <BtnUnderline />
                          <BtnStrikeThrough />
                          <BtnNumberedList />
                          <BtnBulletList />
                          <BtnLink />
                          <BtnClearFormatting />
                          <HtmlButton />
                        </Toolbar>
                      </Editor>
                      {errors?.content && (
                        <span style={{ color: '#FF4842', fontSize: '13px' }}>
                          {errors.content.message}
                        </span>
                      )}
                    </EditorProvider>
                  )}
                />
              </Grid>
            </Grid>
          </form>
        )}

        <div style={{ justifyContent: 'left', paddingTop: '16px' }}>
          <Button
            type="submit"
            form="add-edit-form"
            color="primary"
            variant="contained"
            disabled={loading || formSubmitted}
            sx={{ marginRight: 2 }}
          >
            Save
          </Button>
        </div>

        {snackbarInfo.show && (
          <SnackbarInfo
            type={snackbarInfo.type}
            message={snackbarInfo.message}
          />
        )}
      </Paper>
    </div>
  );
};

ManagePageContent.propTypes = {
  pageType: PropTypes.string.isRequired,
};

export default ManagePageContent;
